<script setup>
import Layout from "../../layouts/main.vue";
import { onMounted, reactive, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import MethodService from "../../service/MethodService";
import DataForm from "./dataServiceGroup";
import mushroom from "cem-primary-api";
import Swal from "sweetalert2";
import toastr from "toastr";
import { useI18n } from "vue-i18n";
import defaultIcon from "@/assets/icon/none-icon-svg.svg";
import { Filter } from "mushroomjs";

const { t } = useI18n();
let loading = ref(false);
const router = useRouter();
const route = useRoute();
const tableRules = reactive(MethodService.copyObject(DataForm.tableRules));
const dataTables = reactive({ value: [] });
const pageSizeDefault = ref(20);
const maxDynamicServiceHealth = ref(0);

const fn_handle = async (type, scope, row) => {
  if (type === "add") {
    router.push({
      name: "ThemMoiServiceHealthGroup",
    });
  }
  if (type === "update") {
    router.push({
      name: "CapNhatServiceHealthGroup",
      params: { id: row.id },
    });
  }
  if (type === "delete") {
    Swal.fire({
      title: t("t_warning"),
      text: t("t_record_delete"),
      icon: "warning",
      confirmButtonColor: "#f46a6a",
      confirmButtonText: t("swal_accept"),
      cancelButtonText: t("swal_cancel"),
      showCancelButton: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const responseGroup = await mushroom.service_health_group.deleteAsync(
            {
              id: row.id,
            },
          );
          if (responseGroup.result) {
            toastr.success(t("toastr_delete_success"));
            await fetchServiceGroupAsync();
          }
        } catch (e) {
          if (e?.code === 400) {
            if (e.subCode === 1005)
              toastr.error(t("toastr_service_group_has_service"));
            else toastr.error(t("toastr_error"));
          } else toastr.error(t("toastr_error"));
        }
      }
    });
  }
};
const fn_showFormTimKiem = () => {
  tableRules.showFormSearch = !tableRules.showFormSearch;
};
const fn_TimKiem = async () => {
  tableRules.dataSearch.value["name"] = tableRules.dataSearch.value["name"]
    ? tableRules.dataSearch.value["name"].trim()
    : "";

  tableRules.filters = MethodService.filterTable(
    JSON.stringify(tableRules.dataSearch),
  );
  tableRules.offset = 0;
  tableRules.sort = "";
  await fetchServiceGroupAsync();
};
const fn_tableSizeChange = (limit) => {
  tableRules.limit = limit;
  pageSizeDefault.value = limit;
  fn_tableChangeOffset(tableRules.page);
};
const fn_tableCurentChange = (page) => {
  fn_tableChangeOffset(page);
};
const fn_tablePrevClick = (page) => {
  fn_tableChangeOffset(page - 1);
};
const fn_tableNextClick = (page) => {
  fn_tableChangeOffset(page + 1);
};
const fn_tableChangeOffset = async (page) => {
  tableRules.page = page;
  tableRules.offset = (tableRules.page - 1) * pageSizeDefault.value;
  await fetchServiceGroupAsync();
};
const fn_tableSortChange = async (column) => {
  MethodService.tableSortChange(column, tableRules);
  loading.value = true;
  await fetchServiceGroupAsync();
};

const fn_hashUrl = () => {
  MethodService.hashUrl(route.query, tableRules);
  pageSizeDefault.value = tableRules.limit;
};

const renderIcon = (iconId) => {
  if (!iconId) return defaultIcon;
  return mushroom.$file.linkBuilder.thumb.id(iconId).build();
};

const fetchServiceGroupAsync = async () => {
  loading.value = true;
  let dataFilter = {
    filters: tableRules.filters,
    fields: tableRules.fields,
    limit: tableRules.limit,
    offset: tableRules.offset,
    sort: tableRules.sort,
  };
  router
    .replace({
      name: "DanhSachServiceHealthGroup",
      query: {
        limit: tableRules.limit,
        page: tableRules.page,
        sort: tableRules.sort,
        filters: tableRules.filters,
        showFormSearch: tableRules.showFormSearch,
      },
    })
    .catch(() => {});
  try {
    const response = await mushroom.service_health_group.listAsync(dataFilter);
    if (response.result) {
      dataTables.value = response.result;
      tableRules.total = response.meta.total;
      tableRules.page = response.meta.offset / response.meta.limit + 1;
    }
  } catch (e) {
    MethodService.showError(e.code);
  } finally {
    loading.value = false;
  }
};

const fetchMaxDynamicServiceHealth = async () => {
  try {
    const { result } = await mushroom.system_config.listAsync({
      filters: Filter.eq("key", "max_dynamic_service_health").build(),
    });
    maxDynamicServiceHealth.value = result[0]?.value || 5;
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

const fn_updateMenuStatus = async (row) => {
  const nextValue = !row.menu;
  const swalText = nextValue ? t("swal_menu_show") : t("swal_menu_hide");
  Swal.fire({
    title: t("t_warning"),
    text: swalText,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept") || "Đồng ý",
    cancelButtonText: t("swal_cancel") || "Hủy",
    allowOutsideClick: false,
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await mushroom.service_health_group.partialUpdateAsync({
          id: row.id,
          menu: nextValue,
        });
        row.menu = nextValue;
        toastr.success(t("toastr_update_success"));
      } catch (e) {
        if (e?.code === 400) {
          if (e.subCode === 1004)
            toastr.error(
              t("t_max_service_health", { msg: maxDynamicServiceHealth.value }),
            );
          else toastr.error(t("toastr_error"));
        } else toastr.error(t("toastr_error"));
      }
    }
  });
};

onMounted(async () => {
  fn_hashUrl();
  await fetchMaxDynamicServiceHealth();
  await fetchServiceGroupAsync();
});
</script>

<template>
  <Layout>
    <div class="card" style="min-height: 80vh">
      <div class="card-header">
        <h3 class="text-uppercase mb-0">{{ $t("t_list_service_group") }}</h3>
        <div class="text-end">
          <div class="btn-header">
            <button
              class="btn btn-soft-secondary btn-border me-2"
              @click="fn_showFormTimKiem()"
            >
              <i class="ri-search-line"></i>
              {{ $t("t-search") }}
            </button>
            <button
              class="btn btn-secondary btn-border"
              @click="fn_handle('add', null)"
            >
              <i class="ri-add-circle-line"></i>
              {{ $t("t-add") }}
            </button>
          </div>
        </div>
      </div>
      <div class="card-body" style="padding: 0 0 15px 0">
        <div class="main-body">
          <div class="main-body-search mx-3 mb-3">
            <el-collapse-transition>
              <div v-show="tableRules.showFormSearch">
                <b-card>
                  <el-form
                    label-position="top"
                    ref="ruleFormRef"
                    @submit.prevent
                    :rules="rulesForm"
                    :model="tableRules.dataSearch.value"
                  >
                    <b-row class="align-items-end">
                      <b-col md="8">
                        <el-form-item :label="$t('t_group_name')" class="mb-0">
                          <el-input
                            clearable
                            v-model="tableRules.dataSearch.value['name']"
                            :placeholder="$t('t_group_name')"
                            @keyup.enter="fn_TimKiem()"
                          ></el-input>
                        </el-form-item>
                      </b-col>
                      <b-col md="4" class="text-end">
                        <button
                          type="button"
                          class="btn btn-soft-secondary btn-border me-2"
                          style="width: 100px"
                          @click="fn_showFormTimKiem()"
                        >
                          {{ $t("t-hide") }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                          style="width: 100px"
                          @click="fn_TimKiem()"
                        >
                          {{ $t("t-search") }}
                        </button>
                      </b-col>
                    </b-row>
                  </el-form>
                </b-card>
              </div>
            </el-collapse-transition>
          </div>
          <div class="mb-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
              @prev-click="fn_tablePrevClick"
              @next-click="fn_tableNextClick"
            />
          </div>
          <el-table
            size="small"
            :data="dataTables.value"
            style="width: 100%"
            max-height="550"
            v-loading="loading"
            :empty-text="$t('t-no-data')"
            @sort-change="fn_tableSortChange"
            :default-sort="{
              prop: tableRules.defaultSort[0],
              order: tableRules.defaultSort[1],
            }"
          >
            <el-table-column
              prop=""
              :label="$t('t-stt')"
              width="70"
              align="center"
            >
              <template #default="scope">
                <div class="text-center">
                  {{ tableRules.offset + scope.$index + 1 }}
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('t_menu')" width="110" align="center">
              <template #default="scope">
                <el-checkbox
                  :model-value="scope.row.menu"
                  @click.stop.prevent="fn_updateMenuStatus(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column
              prop="name"
              :label="$t('t_group_name')"
              min-width="100"
              align="center"
              :sortable="true"
            >
              <template #default="scope">
                <div class="d-flex align-items-center">
                  <img
                    :src="renderIcon(scope.row.icon_id)"
                    alt="icon"
                    class="me-2"
                    style="height: 25px; width: 25px; object-fit: contain"
                  />
                  <span>{{ scope.row.name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="description"
              :label="$t('t_service_group_description')"
              min-width="200"
              align="center"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  {{ scope.row.description }}
                </div>
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('t-action')"
              align="center"
              width="150px"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    :content="$t('t-edit')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-success waves-effect waves-light btn-sm"
                      @click="fn_handle('update', scope.$index, scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    :content="$t('t-delete')"
                    placement="top"
                    :enterable="false"
                    effect="light"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-danger waves-effect waves-light btn-sm"
                      @click="fn_handle('delete', scope.$index, scope.row)"
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div class="mt-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
              @prev-click="fn_tablePrevClick"
              @next-click="fn_tableNextClick"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
